"use client";

import Link from "next/link";
import { useAppSelector } from "@/store/hooks";
import { useCartDetail } from "@/components/hooks/use-cart-detail";
import { useAddProduct } from "@/components/hooks/use-add-to-cart";
import CheckoutHeader from "@/components/checkout/checkout-header";
import OrderSummary from "@/components/checkout/order-summary";
import AddToCartEmptyState from "@/components/add-to-cart/empty-state";
import { isObject } from "@/lib/type-guards";
import type { CartItem } from "@/lib/bagisto/types";
import CartTable from "./cart-table";
import CartPageSkeleton from "./cart-page-skeleton";
import { useSearchParams, useRouter } from "next/navigation";
import { useEffect } from "react";
import { toast } from "sonner";

export default function CartItems() {
  const { isLoading: isCartLoading } = useCartDetail();
  const cartDetail = useAppSelector((state) => state.cartDetail);
  const cart = cartDetail?.cart;
  const { onAddToRemove, onUpdateCart, isUpdateLoading, isRemoveLoading } = useAddProduct();
  const searchParams = useSearchParams();
  const router = useRouter();

  const hasItems = isObject(cart) && cart.items && cart.items.length > 0;

  // Handle error parameter from payment cancellation
  useEffect(() => {
    const error = searchParams.get("error");

    if (error) {
      let errorMessage = "";

      switch (error) {
        case "payment_cancelled":
          errorMessage = "Payment was cancelled. Please try again.";
          break;
        case "payment_declined":
          errorMessage = "Payment was declined. Please try again.";
          break;
        default:
          errorMessage = "An error occurred. Please try again.";
      }

      toast.error(errorMessage);

      // Remove error parameter from URL
      router.replace(window.location.pathname);
    }
  }, []);

  if (isCartLoading) return <CartPageSkeleton />;
  if (!hasItems) return <AddToCartEmptyState />;

  const currencyCode = cart?.cartCurrencyCode || "USD";

  const handleDecrement = (item: CartItem) => {
    const newQty = item.quantity - 1;
    if (newQty < 1) return;
    onUpdateCart({ cartItemId: item.id, quantity: newQty });
  };

  const handleIncrement = (item: CartItem) => {
    onUpdateCart({ cartItemId: item.id, quantity: item.quantity + 1 });
  };

  return (
    <section className="bg-background pt-32 xl:pt-44 px-4 sm:px-6 lg:px-10">
      <div className="max-w-[1440px] mx-auto">

        {/* Breadcrumb + Stepper */}
        <CheckoutHeader />

        {/* Page Title */}
        <div className="mt-9 flex flex-col gap-2">
          <h1 className="font-oswald font-normal text-[20px] leading-[100%] tracking-[-0.02em] uppercase text-neutral-500">
            Cart
          </h1>
          <p className="font-roboto font-medium text-[18px] leading-[25px] tracking-[0%] text-neutral-300">
            {cart.items.length} product{cart.items.length !== 1 ? "s" : ""} in your cart
          </p>
        </div>

        {/* Table */}
        <CartTable
          items={cart.items}
          currencyCode={currencyCode}
          onRemove={onAddToRemove}
          onDecrement={handleDecrement}
          onIncrement={handleIncrement}
          isUpdateLoading={isUpdateLoading}
          isRemoveLoading={isRemoveLoading}
        />

        {/* Order Summary */}
        <OrderSummary
          items={cart.items}
          subTotal={cart.subTotal || "0"}
          currencyCode={currencyCode}
          cartId={cart.id}
          itemsQty={cart.itemsQty}
        >
          <Link
            href="/checkout"
            className="w-full h-[60px] px-5 py-[10px] rounded-[12px] bg-primary-500 hover:bg-primary-500/90 flex items-center justify-center transition-colors"
          >
            <span className="font-oswald font-medium text-[18px] leading-[100%] tracking-[0.2px] uppercase text-center text-white">
              Proceed to Address
            </span>
          </Link>
        </OrderSummary>

      </div>
    </section>
  );
}